/**
 * Closed-beta React Query hooks.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import * as betaApi from "../api/beta";
import { track } from "../analytics";
import { analyticsFailure } from "../analytics-failures";

export const BETA_STATUS_QUERY_KEY = "beta-status";

/** Whether the gate is on. Drives whether invite UI renders at all. */
export function useBetaStatus() {
  return useQuery({
    queryKey: [BETA_STATUS_QUERY_KEY],
    queryFn: () => betaApi.getBetaStatus(),
    // The flag only changes on deploy, so don't re-fetch on every focus.
    staleTime: 5 * 60 * 1000,
  });
}

/** Validate an invite and set the claim cookie. */
export function useClaimBetaInvite() {
  return useMutation({
    mutationFn: (input: { type: betaApi.BetaClaimKind; value: string }) =>
      betaApi.claimBetaInvite(input),
    // Every claim path funnels through this mutation, so this is the one
    // place the funnel needs instrumenting. Props carry the `type` enum only
    // -- never `input.value` (an invite code or household token) and never
    // the error message, which can echo user input. Failure reason is a
    // closed value derived from the structured API code.
    onSuccess: (_data, input) => {
      track("invite_claimed", { type: input.type });
    },
    onError: (error, input) => {
      const failure = analyticsFailure(error, "invite");
      track("invite_claim_failed", {
        type: input.type,
        status: failure.status,
        reason: failure.reason,
      });
    },
  });
}
